import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, CheckCircle, XCircle, AlertTriangle, RefreshCw, Music, Database, Link2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AudioStats {
  totalModules: number;
  modulesWithAudio: number;
  signedUrls: number;
  publicUrls: number;
  readyStatus: number;
  pendingStatus: number;
  errorStatus: number;
}

interface BrokenAudio {
  module_id: string;
  course_id: string;
  audio_url: string;
  status: string;
  error?: string;
}

export const AudioStatusDashboard: React.FC = () => {
  const [stats, setStats] = useState<AudioStats | null>(null);
  const [brokenAudios, setBrokenAudios] = useState<BrokenAudio[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [checkProgress, setCheckProgress] = useState(0);
  const { toast } = useToast();

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      // Get total modules count
      const { count: totalModules } = await supabase
        .from('course_modules')
        .select('*', { count: 'exact', head: true });

      // Get all audio records
      const { data: audioData, error } = await supabase
        .from('module_audio')
        .select('module_id, course_id, audio_url, status');

      if (error) throw error;

      const modulesWithAudio = audioData?.length || 0;
      
      // Categorize URLs
      let signedUrls = 0;
      let publicUrls = 0;
      let readyStatus = 0;
      let pendingStatus = 0;
      let errorStatus = 0;

      audioData?.forEach(audio => {
        if (audio.audio_url?.includes('token=')) {
          signedUrls++;
        } else if (audio.audio_url?.startsWith('http')) {
          publicUrls++;
        }

        if (audio.status === 'ready') readyStatus++;
        else if (audio.status === 'pending' || audio.status === 'processing') pendingStatus++;
        else if (audio.status === 'error') errorStatus++;
      });

      setStats({
        totalModules: totalModules || 0,
        modulesWithAudio,
        signedUrls,
        publicUrls,
        readyStatus,
        pendingStatus,
        errorStatus,
      });
    } catch (error) {
      console.error('Failed to fetch audio stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load audio statistics',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const checkAudioAccessibility = async () => {
    setIsChecking(true);
    setCheckProgress(0);
    setBrokenAudios([]);

    try {
      const { data: audioData } = await supabase
        .from('module_audio')
        .select('module_id, course_id, audio_url, status')
        .eq('status', 'ready')
        .limit(50); // Check first 50 for performance

      if (!audioData || audioData.length === 0) {
        toast({
          title: 'No audio to check',
          description: 'No ready audio files found',
        });
        setIsChecking(false);
        return;
      }

      const broken: BrokenAudio[] = [];
      
      for (let i = 0; i < audioData.length; i++) {
        const audio = audioData[i];
        setCheckProgress(Math.round(((i + 1) / audioData.length) * 100));

        if (!audio.audio_url) {
          broken.push({ ...audio, error: 'No URL' });
          continue;
        }

        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          
          const response = await fetch(audio.audio_url, {
            method: 'HEAD',
            mode: 'cors',
            signal: controller.signal,
          });
          clearTimeout(timeoutId);

          if (!response.ok) {
            broken.push({ ...audio, error: `HTTP ${response.status}` });
          }
        } catch (err) {
          broken.push({ 
            ...audio, 
            error: err instanceof Error ? err.message : 'Unknown error' 
          });
        }
      }

      setBrokenAudios(broken);
      
      toast({
        title: 'Check complete',
        description: `Found ${broken.length} inaccessible audio files out of ${audioData.length} checked`,
        variant: broken.length > 0 ? 'destructive' : 'default',
      });
    } catch (error) {
      console.error('Failed to check audio accessibility:', error);
      toast({
        title: 'Error',
        description: 'Failed to check audio files',
        variant: 'destructive',
      });
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading audio statistics...</span>
        </CardContent>
      </Card>
    );
  }

  const coveragePercent = stats 
    ? Math.round((stats.modulesWithAudio / stats.totalModules) * 100) 
    : 0;

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Music className="h-5 w-5" />
          Audio System Status
        </CardTitle>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchStats}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            variant="default" 
            size="sm" 
            onClick={checkAudioAccessibility}
            disabled={isChecking}
          >
            {isChecking ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Checking ({checkProgress}%)
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-2" />
                Check Accessibility
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Coverage Overview */}
        <div>
          <div className="flex justify-between mb-2 text-sm">
            <span>Audio Coverage</span>
            <span className="font-medium">{stats?.modulesWithAudio} / {stats?.totalModules} modules ({coveragePercent}%)</span>
          </div>
          <Progress value={coveragePercent} className="h-2" />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Database className="h-4 w-4" />
              Total Audio Files
            </div>
            <div className="text-2xl font-bold">{stats?.modulesWithAudio}</div>
          </div>
          
          <div className="bg-green-500/10 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 mb-1">
              <CheckCircle className="h-4 w-4" />
              Ready Status
            </div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats?.readyStatus}</div>
          </div>
          
          <div className="bg-yellow-500/10 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400 mb-1">
              <AlertTriangle className="h-4 w-4" />
              Public URLs
            </div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats?.publicUrls}</div>
            {stats?.publicUrls && stats.publicUrls > 0 && (
              <div className="text-xs text-muted-foreground mt-1">May need conversion</div>
            )}
          </div>
          
          <div className="bg-red-500/10 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 mb-1">
              <XCircle className="h-4 w-4" />
              Error Status
            </div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats?.errorStatus}</div>
          </div>
        </div>

        {/* URL Types */}
        <div className="flex gap-4 text-sm">
          <Badge variant="outline" className="gap-1">
            <CheckCircle className="h-3 w-3 text-green-500" />
            Signed URLs: {stats?.signedUrls}
          </Badge>
          <Badge variant="outline" className="gap-1">
            <AlertTriangle className="h-3 w-3 text-yellow-500" />
            Public URLs: {stats?.publicUrls}
          </Badge>
        </div>

        {/* Broken Audio List */}
        {brokenAudios.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-red-600 dark:text-red-400">
              ⚠️ Inaccessible Audio Files ({brokenAudios.length})
            </h4>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {brokenAudios.map((audio, idx) => (
                <div 
                  key={idx} 
                  className="text-xs bg-red-500/10 rounded px-3 py-2 flex justify-between items-center"
                >
                  <span className="font-mono truncate flex-1">{audio.module_id}</span>
                  <Badge variant="destructive" className="text-xs ml-2">
                    {audio.error}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {isChecking && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Checking audio accessibility...</span>
              <span>{checkProgress}%</span>
            </div>
            <Progress value={checkProgress} className="h-2" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AudioStatusDashboard;
